import type { ChatSummary } from '@medicail/shared';
import { differenceInCalendarDays } from 'date-fns';

export type ChatGroupLabel = 'Today' | 'Yesterday' | 'Previous 7 days' | 'Older';
const ORDER: ChatGroupLabel[] = ['Today', 'Yesterday', 'Previous 7 days', 'Older'];

export function groupLabel(iso: string, now: Date = new Date()): ChatGroupLabel {
  const d = new Date(iso);
  const days = differenceInCalendarDays(now, d);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days <= 7) return 'Previous 7 days';
  return 'Older';
}

/** Group chats (assumed newest first) into the sidebar sections. Empty groups are omitted. */
export function groupChats(chats: readonly ChatSummary[], now: Date = new Date()) {
  const map = new Map<ChatGroupLabel, ChatSummary[]>();
  for (const chat of [...chats].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))) {
    const label = groupLabel(chat.updatedAt, now);
    const list = map.get(label) ?? [];
    list.push(chat);
    map.set(label, list);
  }
  return ORDER.filter((l) => map.has(l)).map((label) => ({ label, chats: map.get(label)! }));
}

export function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function faviconUrl(url: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname(url))}&sz=64`;
}

export function initials(nameOrEmail: string): string {
  const base = nameOrEmail.split('@')[0] ?? nameOrEmail;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0]![0]! + parts[1]![0]! : base.slice(0, 2);
  return letters.toUpperCase();
}
